import { useState } from "react";
import { Equipment, EquipmentBase } from "../../../model/character";
import { v4 } from 'uuid';

import { Box, Button, Chip, FormGroup, Stack, TextField, Typography } from "@mui/material";
import { NumberField } from "../../../components/formFields/NumberField";
import FormFieldLabel from "../../../components/formFields/FormFieldLabel";

import { useNavigate, useParams } from "react-router-dom";
import { exportData, loadLibraryData, saveLibraryData } from "../../../master/libraryData";
import Page from "../../../components/Page";
import ButtonFlat from "../../../components/ButtonFlat";
import { StatDisplay } from "../../../components/StatDisplay";
import { ModalContentSection } from "../../../components/Modal";
import AbilityEditor from "../../characterPage/AbilityEditor";
import SelectField from "../../../components/formFields/SelectField";

const defaultEquipment: Equipment = {
    id: "",
    description: "",
    acquire: 0,
    adjustments: [],
    features: [],
    influence: 0,
    knowledge: 0,
    resources: 0,
    slots: 1,
    size: "standard",
    name: "New Utility",
    strongHits: [],
    isStashed: false,
    draw: 0,
}

export const GetNew = (name: string): Equipment => {
    return {
        ...defaultEquipment,
        id: v4(),
        name: name
    }
}

export interface EquipmentEditorProps {
    prefix: string;
    pathType: string;
    defaultName: string;
}

export const EquipmentEditor = (props: EquipmentEditorProps) => {
    const { prefix, pathType, defaultName } = props;
    const { id } = useParams();
    const navigate = useNavigate();
    const [ current, setCurrent ] = useState<Equipment>(loadLibraryData<Equipment>(prefix, id ?? "NONE") ?? GetNew(defaultName));
    const [ feature, setFeature ] = useState("");

    const handleSave = () => {
        navigate(`/library/${pathType}/${current.id}`, {replace: true});
        saveLibraryData(prefix, current);
    }

    const handleBack = () => {
        navigate("/library");
    }

    const handleExport = () => {
        exportData(current, prefix);
    }

    const handleAddFeature = () => {
        const newFeatures = [ ...current.features, feature ];
        onChange("features", newFeatures);
        setFeature("");
    }

    const handleRemoveFeature = (index: number) => {
        const features = [ ...current.features ];
        features.splice(index, 1);
        onChange("features", features);
    }

    const onChange = (key: string, value: any) => {
        setCurrent((c) => ({
            ...c,
            [key]: value
        }))
    }

    return <Page>
        <h2>{current.name}</h2>
        <FormGroup>
            <Stack spacing={1}>
            <FormFieldLabel label="Name">
                <TextField type="text" variant="standard" value={current.name} onChange={(event) => onChange("name", event.currentTarget.value)} />
            </FormFieldLabel>
            <SelectField value={current.size} onChange={(value) => onChange("size", value)} options={[
                    { name: `Size - None (0)`, value: "none" },
                    { name: `Size - Trinket (0.25)`, value: "trinket" },
                    { name: `Size - Small (1)`, value: "small" } ,
                    { name: `Size - Standard (2)`, value: "standard" } ,
                    { name: `Size - Large (3)`, value: "large" } ,
                ]} />
            <FormFieldLabel label="Slots" justifyContent="end">
                <NumberField step={1} value={current.slots} onChange={(value) => onChange("slots", value)} customRenderer={(v) => StatDisplay(v, "" , true)} />
            </FormFieldLabel>
            <FormFieldLabel label="Draw" justifyContent="end">
                <NumberField min={0} step={1} value={current.draw} onChange={(value) => onChange("draw", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Acquire" justifyContent="end">
                <NumberField step={1} value={current.acquire} onChange={(value) => onChange("acquire", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Knowledge" justifyContent="end">
                <NumberField step={1} value={current.knowledge} onChange={(value) => onChange("knowledge", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Influence" justifyContent="end">
                <NumberField step={1} value={current.influence} onChange={(value) => onChange("influence", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Resources" justifyContent="end">
                <NumberField step={1} value={current.resources} onChange={(value) => onChange("resources", value)} />
            </FormFieldLabel>
            </Stack>
            <ModalContentSection title="Keywords">
                <Stack direction="row">
                    <TextField sx={{flexGrow: 1}} type="text" variant="standard" value={feature} onChange={(event) => setFeature(event.currentTarget.value)} />
                    <Button onClick={handleAddFeature}>Add Feature</Button>
                </Stack>
                <Stack flexWrap="wrap" useFlexGap direction="row" spacing={1}>
                    {current.features.map((value, index) => <Chip onClick={() => handleRemoveFeature(index)} label={value} key={index} />)}
                </Stack>
            </ModalContentSection>
            <AbilityEditor title="Passive Abilities" abilities={current.strongHits} setAbilities={(value) => onChange("strongHits", value)} />
            <Box>
                <Typography>Notes</Typography>
                <TextField value={current.description} onChange={(event) => onChange('description', event.currentTarget.value)} multiline fullWidth />
            </Box>
            <Stack direction={"row"} spacing={1} paddingTop={1}>
                <ButtonFlat onClick={handleSave}>Save</ButtonFlat>
                <ButtonFlat onClick={handleBack}>Back to Library</ButtonFlat>
                <ButtonFlat onClick={handleExport}>Export</ButtonFlat>
            </Stack>
        </FormGroup>
    </Page>
}